"use client";

import type React from "react";
import { cn } from "../lib/utils";
import { useTranslation } from "react-i18next";
import { MessageSquare, Users } from "lucide-react";
import { Badge } from "./ui/badge";
import { useSession } from "../context/SessionContext";
import { QuickAssignPopover } from "./QuickAssignPopover";
import { useReassign } from "../lib/useChatPage";
import { useState } from "react";
import { Tooltip } from "./ui/tooltip";
import { TooltipProvider, TooltipTrigger, TooltipContent } from "./ui/tooltip";
import { getAgentAbbreviation } from "../lib/chatUtils";

interface NotificationItemProps {
  notification: any;
  user: any;
  isActive: boolean;
  isUrgent: boolean;
  onNotificationClick: (
    userId: string | number,
    phone: string,
    type: string,
    urgent: boolean,
    date: Date,
    important: boolean,
    ignored: boolean,
    read: boolean,
    collaborations: any[],
    assigned_by: string,
    transferId?: number
  ) => void;
  getRelativeTime: (dateString: string) => { label: string; isStale: boolean };
  assistants?: any[];
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  user,
  isActive,
  isUrgent,
  onNotificationClick,
  getRelativeTime,
  assistants = [],
}) => {
  const { label, isStale } = getRelativeTime(notification.date);
  const { t } = useTranslation();
  const { setSidebarAction, session,
    allAssistants,
    userInfo,
    fetchPendingTransfers,
    fetchAllUsersInfo,
    setActiveNotification,
    setActivePhone,
    setSelectedConversationId, } =
    useSession();
    
  const liClasses = cn(
    "relative rounded-sm border bg-white p-3  hover:shadow-md transition mb-1 cursor-pointer",
    isActive 
      ? "border-[#105BDB] ring-1 ring-[#105BDB] ring-opacity-20" 
      : "border-[#E5E7EB] hover:border-[#D1D5DB]"
  );

  const showCount = !isActive && Number(notification.newCount) > 0;

  const chipBase =
    "inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium";
  const chipUrgent = "bg-[#FBE9EB] text-[#DC3545]";
  const chipNormal = "bg-[#EEF2F7] text-[#4A5364]";

  const assignedAssistant = assistants.find(
    (a: any) => String(a.id) === String(notification.assigned_to)
  );
  const assignedDisplay = assignedAssistant?.name 
    ? getAgentAbbreviation(assignedAssistant.name, false)
    : "—";

  const [qaAssistantId, setQaAssistantId] = useState<string | null>(null);
  const [qaNote, setQaNote] = useState("");


  const { reassignNotification } = useReassign({
    activeNotification: notification, 
    session,
    allAssistants,
    userInfo,
    selectedAssistantId: qaAssistantId,
    reassignNote: qaNote,
    fetchPendingTransfers,
    fetchAllUsersInfo,
    setActiveNotification,
    setActivePhone,
    setSelectedConversationId,
  });

  const isAssignedToCurrentUser = String(notification.assigned_to) === String(session?.user?.id);

  const handlePopoverOpen = () => {
    if (!isActive) {
      onNotificationClick(
        notification.userId,
        user?.phone || "",
        notification.type,
        isUrgent,
        notification.date,
        notification.important,
        notification.ignored,
        notification.read,
        notification.collaborations,
        notification.assigned_by,
        notification.transferId
      );
    }
  };

  const handleQuickAssign = async (assistantId: string, note: string) => {
    setQaAssistantId(assistantId);
    setQaNote(note);
    setActiveNotification?.(notification);
    if (user?.phone) setActivePhone?.(user.phone);
    await reassignNotification(assistantId);
  };

  const handleDirectClick = () => {
    onNotificationClick(
      notification.userId,
      user?.phone || "",
      notification.type,
      isUrgent,
      notification.date,
      notification.important,
      notification.ignored,
      notification.read,
      notification.collaborations,
      notification.assigned_by,
      notification.transferId
    );
  };

  const goResolve = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveNotification?.(notification);
    if (user?.phone) setActivePhone?.(user.phone);
    setSidebarAction?.({ type: "resolve" });
  };

  const goIgnore = (e: React.MouseEvent) => {
    e.stopPropagation();
    setActiveNotification?.(notification);
    if (user?.phone) setActivePhone?.(user.phone);
    setSidebarAction?.({ type: "ignore" });
  };

  return (
    <li
      className={liClasses}
      onClick={handleDirectClick}
    >
      <div className="flex items-center justify-between">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild data-tooltip-trigger>
              <span className={cn(chipBase, isUrgent ? chipUrgent : chipNormal)}>
                {isUrgent ? (
                  t("urgent")
                ) : (
                  <span className="block max-w-[120px] truncate">
                    {notification.content || t("new_message_received")}
                  </span>
                )}
              </span>
            </TooltipTrigger>
            <TooltipContent>
              {notification.content || t("new_message_received")}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <span
          className={cn(
            "text-xs",
            isStale || isUrgent ? "text-[#DC3545]" : "text-[#6F7D95]"
          )}
        >
          {label}
        </span>
      </div>

      <div className="mt-2">
        <div className="text-[15px] font-semibold text-[#252A32]">
          {user?.name && user.name !== "null" && String(user.name).trim() !== ""
            ? user.name
            : user?.nickname && String(user.nickname).trim() !== "" && user.nickname !== "null"
            ? user.nickname
            : t("unauthenticated_user")}
        </div>
        <div className="text-xs text-[#6F7D95]">{user?.phone}</div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        {notification.type === "transfer" && isAssignedToCurrentUser && (
          <div
            className="inline-flex items-center rounded-lg border border-[#E5E7EB] bg-white text-[#4A5364] shadow-sm select-none"
            role="group"
          >
            <button
              type="button"
              onClick={goResolve}
              className="px-[8px] py-[6px] text-xs font-medium rounded-l-full hover:bg-[#F6F7F9] focus:outline-none"
            >
              {t("resolve")}
            </button>

            <span aria-hidden className="h-4 w-px bg-[#E5E7EB]" />

            <button
              type="button"
              onClick={goIgnore}
              className="px-[8px] py-[6px] text-xs font-medium rounded-r-full hover:bg-[#F6F7F9] focus:outline-none"
            >
              {t("mark_as_ignored")}
            </button>
          </div>
        )}

        <div className={cn("flex items-center gap-3", !isAssignedToCurrentUser && notification.type === "transfer" && "ml-auto")}>
          <div className="relative inline-flex">
            <MessageSquare
              className={cn(
                "w-8 h-8 px-[8px] py-[6px] text-[#4A5364] border rounded-xl"
              )}
            />
            {showCount && (
              <Badge
                variant="destructive"
                className="absolute -top-2 -right-2 h-5 min-w-[1.25rem] px-1 py-0 text-[10px] leading-5 flex items-center justify-center rounded-full"
              >
                {notification.newCount}
              </Badge>
            )}
          </div>

          <QuickAssignPopover
            assistants={allAssistants || assistants}
            sessionUserId={session?.user?.id ?? null}
            disabledAssistantId={String(notification.assigned_to ?? "")}
            onConfirm={handleQuickAssign}
            onOpen={handlePopoverOpen}
            isNotificationActive={isActive}
            trigger={
              <div 
                className={cn(
                  "inline-flex items-center gap-1 px-[8px] py-[6px] text-[#4A5364] border rounded-xl text-sm cursor-pointer active:bg-[#F6F7F9]"
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <Users className="w-4 h-4" />
                <span>{assignedDisplay}</span>
              </div>
            }
          />
        </div>
      </div>
    </li>
  );
};
